import { DatePipe, TitleCasePipe } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  FinancialControllerService,
  Gateway,
  PageableFilter,
  SubscriptionType,
  SubscriptionTypeGatewayPriority,
  SubscriptionTypePriceControllerService
} from 'src/app/admin-api';
import { SubscriptionTypeControllerService } from 'src/app/admin-api/api/subscriptionTypeController.service';
import { SubscriptionTypePrice } from 'src/app/admin-api/model/subscriptionTypePrice';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { Role, roleAsObject } from '../../../../models/enums/Role';
import { SubscriptionTypePriceFormModalComponent } from './subscription-type-price-form-modal/subscription-type-price-form-modal.component';

@Component({
  selector: 'app-subscription-type-details',
  templateUrl: './subscription-type-details.component.html',
  styleUrl: './subscription-type-details.component.scss',
  providers: [DatePipe, DialogService, ConfirmationService, MessageService]
})
export class SubscriptionTypeDetailsComponent implements OnInit {
  private title = inject(Title);
  public subscriptionTypeService = inject(SubscriptionTypeControllerService);
  public subscriptionTypePriceController = inject(
    SubscriptionTypePriceControllerService
  );
  private financialService = inject(FinancialControllerService);
  private datePipe = inject(DatePipe);
  private dialog = inject(DialogService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private titleCasePipe = inject(TitleCasePipe);
  private ngxPermissionsService = inject(NgxPermissionsService);

  @Input()
  subscriptionTypeId: number;

  subscriptionType: SubscriptionType | undefined;

  actionButtons: Array<TableActionButton> = [
    new TableActionButton(
      '',
      'remove',
      PrimeIcons.TRASH,
      (price: SubscriptionTypePrice) => !price.changed,
      '',
      'Excluir',
      'bottom',
      true,
      true,
      'danger',
      'small'
    )
  ];

  cols: Array<TableColumn> = [
    new TableColumn.Builder()
      .setHeader('Preço antigo')
      .setField('oldPrice')
      .setFilter(true)
      .setType('currency')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço novo')
      .setField('newPrice')
      .setFilter(true)
      .setType('currency')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data de alteração (Aquisição/Reativação)')
      .setField('dateChanged')
      .setFilter(true)
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((price: SubscriptionTypePrice) =>
        price?.dateChanged
          ? this.datePipe.transform(
              FormUtil.utcDate(price.dateChanged),
              'dd/MM/yyyy'
            )
          : null
      )
      .setStyleClass('text-right')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data de alteração (Troca de plano)')
      .setField('maxDateToRenew')
      .setFilter(true)
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((price: SubscriptionTypePrice) =>
        price?.dateChanged
          ? this.datePipe.transform(
              FormUtil.utcDate(price.maxDateToRenew),
              'dd/MM/yyyy'
            )
          : null
      )
      .setStyleClass('text-right')
      .build(),
    new TableColumn.Builder()
      .setHeader('Ações')
      .setField('')
      .setType('button')
      .build()
  ];
  fixedFilters: Array<PageableFilter>;
  prices: Array<SubscriptionTypePrice>;
  gateways: SubscriptionTypeGatewayPriority[];
  gatewayForm: FormArray<FormControl<Gateway>>;
  gatewayList: Gateway[];
  gatewaysAvailable: Gateway[][];
  canAddGateway = false;
  permissions = false;

  async ngOnInit(): Promise<void> {
    try {
      this.permissions = await this.ngxPermissionsService.hasPermission(
        roleAsObject(Role.Full_Administrator).enumValue
      );
    } catch (error) {
      this.permissions = false;
    }
    if (this.subscriptionTypeId) {
      LoaderService.showLoader();
      await Promise.all([
        this.findSubscriptionType(this.subscriptionTypeId),
        this.findPrices(this.subscriptionTypeId),
        this.findGateways(),
        this.findGatewayPriorities(this.subscriptionTypeId)
      ]);
      this.startGatewayForm();
      this.updateAvailableGateways();
      LoaderService.showLoader(false);
      this.title.setTitle(
        'Plano - ' + this.titleCasePipe.transform(this.subscriptionType.name)
      );
    } else {
      AppDialogService.showErrorDialog(
        { message: 'Plano não encontrado' },
        true
      );
    }
  }

  openNewPriceModal(): void {
    this.dialog
      .open(SubscriptionTypePriceFormModalComponent, {
        closable: true,
        closeOnEscape: true,
        header: `Agendar troca de preço`,
        data: {
          subscriptionType: this.subscriptionType
        },
        modal: true,
        width: '400px'
      })
      .onClose.subscribe(async (data: SubscriptionTypePrice) => {
        if (data) {
          this.messageService.add({
            severity: 'success',
            detail: 'Agendamento criado com sucesso.',
            summary: 'Sucesso'
          });
          await this.findPrices(this.subscriptionType?.subscriptionTypeId);
          LoaderService.showLoader(false);
        }
      });
  }

  callAction(event: {
    item: SubscriptionTypePrice;
    $event: Event;
    action: 'remove';
  }): void {
    if (event.action === 'remove') {
      const subscriptionTypePriceId = event.item.subscriptionTypePriceId;

      this.confirmationService.confirm({
        header: 'Exclusão',
        message: 'Tem certeza que deseja excluir?',
        acceptLabel: 'Sim',
        rejectLabel: 'Não',
        accept: async () => {
          await this.deletePrice(subscriptionTypePriceId);
        },
        target: event.$event.target
      });
    }
  }

  async deletePrice(subscriptionTypePriceId: number) {
    LoaderService.showLoader();
    try {
      const detail = await lastValueFrom(
        this.subscriptionTypePriceController
          .deleteSubscriptionTypePrice(subscriptionTypePriceId)
          .pipe(map((data) => data.result))
      );
      this.messageService.add({
        severity: 'success',
        detail,
        summary: 'Sucesso'
      });
      await this.findPrices(this.subscriptionType?.subscriptionTypeId);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }

  async findPrices(subscriptionTypeId: number): Promise<void> {
    try {
      this.prices = await lastValueFrom(
        this.subscriptionTypePriceController
          .getAllSubscriptionTypePriceBySubscriptionTypeId(subscriptionTypeId)
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findSubscriptionType(subscriptionTypeId: number): Promise<void> {
    try {
      this.subscriptionType = await lastValueFrom(
        this.subscriptionTypeService
          .findSubscriptionType(subscriptionTypeId)
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findGateways(): Promise<void> {
    try {
      this.gatewayList = await lastValueFrom(
        this.financialService
          .findGatewaysList()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findGatewayPriorities(subscriptionTypeId: number): Promise<void> {
    try {
      this.gateways = await lastValueFrom(
        this.subscriptionTypeService
          .getGatewayPriorityBySubscriptionTypeId(subscriptionTypeId)
          .pipe(map((data) => data.result ?? []))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  startGatewayForm() {
    const controls = (this.gateways ?? []).map(
      (g) =>
        new FormControl(
          {
            value:
              this.gatewayList.find((gw) => gw.gatewayId === g.gatewayId) || g,
            disabled: !this.permissions
          },
          Validators.required
        )
    );

    if (controls.length === 0 && this.permissions) {
      controls.push(new FormControl(null, Validators.required));
    }

    this.gatewayForm = new FormArray(controls);
  }

  async saveGatewayPriorities(): Promise<void> {
    try {
      LoaderService.showLoader();
      const gatewayIds = this.gatewayForm
        .getRawValue()
        .filter((gateway: Gateway | null): gateway is Gateway => gateway != null)
        .map((gateway: Gateway) => gateway.gatewayId)
        .filter((gatewayId: number | null | undefined): gatewayId is number => gatewayId != null);

      this.gateways = await lastValueFrom(
        this.subscriptionTypeService
          .updateGatewayPriorityBySubscriptionTypeId(
            gatewayIds,
            this.subscriptionTypeId
          )
          .pipe(map((data) => data.result ?? []))
      );
      this.startGatewayForm();
      this.updateAvailableGateways();
      this.messageService.add({
        severity: 'success',
        detail: 'Gateways atualizados com sucesso.',
        summary: 'Sucesso'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  addNewGateway(): void {
    if (!this.canAddGateway) {
      return;
    }
    this.gatewayForm.push(new FormControl(null, Validators.required));
    this.updateAvailableGateways();
  }

  removeGateway(index: number): void {
    if (this.gatewayForm.controls.length <= 1) {
      return;
    }
    this.gatewayForm.removeAt(index);
    this.updateAvailableGateways();
  }

  updateAvailableGateways(): void {
    const selectedValues = this.gatewayForm.getRawValue() as Array<Gateway | null>;
    this.gatewaysAvailable = selectedValues.map((selectedGateway) => {
      return this.gatewayList.filter(
        (g) =>
          g.gatewayId === selectedGateway?.gatewayId ||
          !selectedValues.some(
            (sg) => sg?.gatewayId === g.gatewayId
          )
      );
    });
    this.canAddGateway =
      this.permissions &&
      !!this.gatewayList?.length &&
      this.gatewayForm.controls.length < this.gatewayList.length;
  }

  get scheduled(): SubscriptionTypePrice {
    return this.prices?.find(
      (p) =>
        FormUtil.utcDate(p.dateChanged).getTime() >
        FormUtil.utcDate(new Date()).getTime()
    );
  }

  get scheduledDate(): string {
    return this.scheduled
      ? FormUtil.daysDiffLabel(
          new Date(),
          FormUtil.utcDate(this.scheduled.dateChanged)
        )
      : null;
  }

  get lastScheduled(): SubscriptionTypePrice {
    return !this.scheduled && this.prices
      ? this.prices.reduce((last, p) => {
          if (
            FormUtil.utcDate(p.dateChanged).getTime() >
            FormUtil.utcDate(last.dateChanged).getTime()
          ) {
            last = p;
          }
          return last;
        }, this.prices[0])
      : null;
  }

  get lastScheduledDate(): string {
    return this.lastScheduled
      ? FormUtil.daysDiffLabel(new Date(), this.lastScheduled.dateChanged)
      : null;
  }

  get gatewaysToChoose(): Gateway[] {
    return this.gatewayList.filter(
      (g) =>
        !this.gatewayForm.value.some(
          (selectedGateway: Gateway) =>
            selectedGateway.gatewayId === g.gatewayId
        )
    );
  }
}
